// packages/web/src/components/PilotForm.tsx — misma lógica (búsqueda de alumno, cascada, multi-OA, pregen SSE, submit).
// Cambios: 3 secciones numeradas (Alumno · Qué evaluar · Cómo), labels asociados, modalidad como
// radio cards, opciones de IA plegadas, barra inferior con progreso de la matriz + botón.
import { useCallback, useEffect, useId, useState } from 'react';
import { ArrowRight, Loader2, AlertCircle, CheckCircle2, ChevronDown } from 'lucide-react';
import type { LessonMaterialAttach } from '@marzano/contracts';
import { LearnerSearchInput } from './LearnerSearchInput';
import { LessonMaterialUploader } from './LessonMaterialUploader';
import { Alert, Button, Card, INPUT_CLASS, PageHeader } from './ui';
import { api, type LearnerProfileFull, type PionerosLearningObjective, type PionerosMilestone, type PionerosSubject } from '../api';
import { MODALITY_LABEL, MODALITY_MAX_MIN, OA_TIPO_LABEL, type Modality, type OATipo, type PilotInput } from '../types';
import { useMatrixPregeneration } from '../hooks/useMatrixPregeneration';
import type { EnrichmentTipo } from '../api';

const OA_TIPO_TO_ENRICHMENT: Record<OATipo, EnrichmentTipo> = {
  conceptual: 'conceptual', procedural: 'procedimental', argumentative: 'argumentativo', investigative: 'investigativo',
};

interface PilotFormProps {
  defaultSiteId: string;
  presetLearner?: LearnerProfileFull | null;
  onSubmit: (input: PilotInput) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function PilotForm({ defaultSiteId, presetLearner, onSubmit, isLoading, error }: PilotFormProps) {
  const ids = { subj: useId(), mile: useId(), tipo: useId(), hint: useId() };
  const [learner, setLearner] = useState<LearnerProfileFull | null>(presetLearner ?? null);
  useEffect(() => { if (presetLearner) setLearner(presetLearner); }, [presetLearner]);

  const [subjects, setSubjects] = useState<PionerosSubject[]>([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<PionerosSubject | null>(null);
  const [milestones, setMilestones] = useState<PionerosMilestone[]>([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState<PionerosMilestone | null>(null);
  const [selectedOAs, setSelectedOAs] = useState<PionerosLearningObjective[]>([]);
  const primarySelectedOA = selectedOAs[0] ?? null;
  const [modality, setModality] = useState<Modality>('written');
  const [oaTipo, setOaTipo] = useState<OATipo>('conceptual');
  const [lessonMaterial, setLessonMaterial] = useState<LessonMaterialAttach | null>(null);
  const [teacherHint, setTeacherHint] = useState('');

  const levelName = learner?.level_label ?? null;
  const levelMongoId = learner?.level_id ?? null;

  useEffect(() => {
    setSelectedSubject(null); setSelectedMilestone(null); setSelectedOAs([]); setMilestones([]);
    if (!learner || !levelName) { setSubjects([]); return; }
    let cancelled = false;
    setSubjectsLoading(true);
    api.listSubjects(defaultSiteId, levelName)
      .then(({ subjects }) => { if (!cancelled) setSubjects(subjects); })
      .catch(() => { if (!cancelled) setSubjects([]); })
      .finally(() => { if (!cancelled) setSubjectsLoading(false); });
    return () => { cancelled = true; };
  }, [learner, levelName, defaultSiteId]);

  useEffect(() => {
    setSelectedMilestone(null); setSelectedOAs([]);
    if (!levelMongoId || !selectedSubject) { setMilestones([]); return; }
    let cancelled = false;
    setMilestonesLoading(true);
    api.listMilestones(defaultSiteId, levelMongoId, selectedSubject._id)
      .then(({ milestones }) => { if (!cancelled) setMilestones(milestones); })
      .catch(() => { if (!cancelled) setMilestones([]); })
      .finally(() => { if (!cancelled) setMilestonesLoading(false); });
    return () => { cancelled = true; };
  }, [levelMongoId, selectedSubject, defaultSiteId]);

  useEffect(() => { setSelectedOAs([]); }, [selectedMilestone]);

  const siteId: string = learner?.local_site_ids?.[0] !== undefined ? String(learner.local_site_ids[0]) : defaultSiteId;
  const hasMissingSiteMapping = learner !== null && (learner.local_site_ids?.length ?? 0) === 0;
  const formValid = learner !== null && !hasMissingSiteMapping && selectedSubject !== null && selectedMilestone !== null && selectedOAs.length > 0;
  const hitoDescripcionResolved = selectedMilestone?.mastery_criteria && selectedMilestone.mastery_criteria.length >= 10 ? selectedMilestone.mastery_criteria : selectedMilestone?.name ?? '';

  const pregenInputs = formValid && primarySelectedOA && selectedMilestone && selectedSubject
    ? { siteId, oa_codigo: primarySelectedOA.code, oa_descripcion: primarySelectedOA.description, asignatura: selectedSubject.name,
        nivel_academico: learner?.level_label ?? '', hito_nombre: selectedMilestone.name, hito_descripcion: hitoDescripcionResolved, tipo: OA_TIPO_TO_ENRICHMENT[oaTipo] }
    : null;
  const pregen = useMatrixPregeneration(pregenInputs);

  const doSubmit = useCallback(() => {
    if (!formValid || !learner || !selectedSubject || !selectedMilestone || selectedOAs.length === 0 || isLoading) return;
    const studentName = learner.name ?? learner.email.split('@')[0] ?? 'Estudiante';
    const pregenId = selectedOAs.length === 1 && pregen.status === 'ready' ? pregen.enrichment?.id : undefined;
    const primaryOa = selectedOAs[0]!;
    void onSubmit({
      site_id: siteId, student_name: studentName, student_email: learner.email, asignatura: selectedSubject.name,
      nivel_academico: learner.level_label ?? '', oa_codigo: primaryOa.code, oa_descripcion: primaryOa.description,
      hito_nombre: selectedMilestone.name, hito_descripcion: hitoDescripcionResolved, modality, oa_tipo: oaTipo,
      oas: selectedOAs.map((o) => ({ oa_codigo: o.code, oa_descripcion: o.description, tipo: oaTipo })),
      ...(pregenId !== undefined ? { enrichment_id: pregenId } : {}),
      ...(lessonMaterial ? { lesson_material: lessonMaterial } : {}),
      ...(teacherHint.trim() && { teacher_hint: teacherHint.trim() }),
    });
  }, [formValid, learner, selectedSubject, selectedMilestone, selectedOAs, isLoading, siteId, modality, oaTipo, hitoDescripcionResolved, pregen.status, pregen.enrichment, lessonMaterial, teacherHint, onSubmit]);

  const submit = (e: React.FormEvent) => { e.preventDefault(); doSubmit(); };
  const pregenInFlight = pregen.status === 'streaming' || (formValid && pregen.status === 'idle');

  return (
    <form onSubmit={submit} className="max-w-4xl mx-auto space-y-5 pb-24">
      <PageHeader title="Evaluación individual" subtitle="Busca al alumno, elige qué evaluar y cómo. La matriz curricular se prepara mientras completas el formulario." />

      <Card title="1. Alumno">
        <LearnerSearchInput siteId={defaultSiteId} value={learner} onChange={setLearner} label="Alumno" placeholder="Nombre (mín. 2 letras) o email completo" required />
        {hasMissingSiteMapping && (
          <Alert kind="warn">La sede de este alumno aún no está sincronizada con Marzano. No se puede iniciar hasta que el proceso <code className="font-mono">mongo-refresh-catalogs</code> la agregue.</Alert>
        )}
        {learner && !hasMissingSiteMapping && (
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <Derived label="Nivel" value={learner.level_label ?? '—'} />
            <Derived label="Sede" value={String(learner.sites_labels?.[0] ?? learner.local_site_ids[0] ?? '—')} />
            <Derived label="Email" value={learner.email} mono />
          </dl>
        )}
      </Card>

      <Card title="2. Qué vas a evaluar">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor={ids.subj} className="block text-sm font-medium text-slate-800">Asignatura <span className="text-red-700" aria-hidden>*</span></label>
            <div className="relative">
              <select id={ids.subj} className={INPUT_CLASS + ' appearance-none pr-9'} value={selectedSubject?._id ?? ''} disabled={!learner || subjects.length === 0}
                onChange={(e) => setSelectedSubject(subjects.find((x) => x._id === e.target.value) ?? null)}>
                <option value="">{!learner ? 'Primero elige un alumno' : subjectsLoading ? 'Cargando…' : subjects.length === 0 ? 'Sin asignaturas para este nivel' : 'Elige una asignatura'}</option>
                {subjects.map((s) => <option key={s._id} value={s._id}>{s.name}{s.is_core ? ' (core)' : ''}</option>)}
              </select>
              {subjectsLoading ? <Loader2 aria-hidden className="w-4 h-4 animate-spin text-slate-500 absolute right-3 top-[60%] -translate-y-1/2" /> : <ChevronDown aria-hidden className="w-4 h-4 text-slate-500 absolute right-3 top-[60%] -translate-y-1/2 pointer-events-none" />}
            </div>
          </div>
          <div>
            <label htmlFor={ids.mile} className="block text-sm font-medium text-slate-800">Hito <span className="text-red-700" aria-hidden>*</span></label>
            <div className="relative">
              <select id={ids.mile} className={INPUT_CLASS + ' appearance-none pr-9'} value={selectedMilestone?._id ?? ''} disabled={!selectedSubject || milestones.length === 0}
                onChange={(e) => setSelectedMilestone(milestones.find((x) => x._id === e.target.value) ?? null)}>
                <option value="">{!selectedSubject ? 'Primero elige una asignatura' : milestonesLoading ? 'Cargando…' : milestones.length === 0 ? 'Sin hitos para esta combinación' : `Elige un hito (${milestones.length})`}</option>
                {milestones.map((m) => <option key={m._id} value={m._id}>{m.name.slice(0, 90)}{m.name.length > 90 ? '…' : ''}{m.oas.length > 0 ? ` · ${m.oas.length} OA${m.oas.length === 1 ? '' : 's'}` : ''}</option>)}
              </select>
              {milestonesLoading ? <Loader2 aria-hidden className="w-4 h-4 animate-spin text-slate-500 absolute right-3 top-[60%] -translate-y-1/2" /> : <ChevronDown aria-hidden className="w-4 h-4 text-slate-500 absolute right-3 top-[60%] -translate-y-1/2 pointer-events-none" />}
            </div>
          </div>
        </div>

        {selectedMilestone && (
          <div className="rounded-lg bg-slate-50 px-3.5 py-3">
            <p className="text-sm font-medium text-slate-900">{selectedMilestone.name}</p>
            <p className="text-[13px] text-slate-600 mt-0.5">
              {[selectedMilestone.evaluation_type, selectedMilestone.evaluation_category, selectedMilestone.mastery_criteria && `criterio ${selectedMilestone.mastery_criteria}`,
                selectedMilestone.due_date && `vence ${new Date(selectedMilestone.due_date).toLocaleDateString('es-CL')}`].filter(Boolean).join(' · ')}
            </p>
          </div>
        )}

        {selectedMilestone && (
          <fieldset>
            <div className="flex items-baseline justify-between mb-2">
              <legend className="text-sm font-medium text-slate-800">Objetivos de aprendizaje <span className="text-red-700" aria-hidden>*</span> <span className="font-normal text-slate-600">· {selectedOAs.length} de {selectedMilestone.oas.length}</span></legend>
              {selectedMilestone.oas.length > 1 && (
                <button type="button" className="text-[13px] font-semibold text-marzano-600 hover:underline"
                  onClick={() => setSelectedOAs(selectedOAs.length === selectedMilestone.oas.length ? [] : [...selectedMilestone.oas])}>
                  {selectedOAs.length === selectedMilestone.oas.length ? 'Quitar todos' : 'Elegir todos'}
                </button>
              )}
            </div>
            {selectedMilestone.oas.length === 0 ? (
              <p className="text-sm text-slate-600 rounded-lg border border-dashed border-slate-300 px-3 py-4 text-center">Este hito no tiene OAs asociados.</p>
            ) : (
              <ul className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                {selectedMilestone.oas.map((o) => {
                  const checked = selectedOAs.some((x) => x._id === o._id);
                  return (
                    <li key={o._id}>
                      <label className={`flex items-start gap-3 px-3.5 py-3 cursor-pointer ${checked ? 'bg-marzano-50' : 'hover:bg-slate-50'}`}>
                        <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-400 text-marzano-600 focus:ring-marzano-500" checked={checked}
                          onChange={() => setSelectedOAs((prev) => (checked ? prev.filter((x) => x._id !== o._id) : [...prev, o]))} />
                        <span className="text-sm leading-relaxed"><span className="font-mono text-[13px] font-semibold text-marzano-700 bg-white rounded px-1.5 py-0.5 mr-1.5 border border-marzano-100">{o.code}</span>{o.description}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </fieldset>
        )}

        {selectedMilestone && (
          <div className="max-w-sm">
            <label htmlFor={ids.tipo} className="block text-sm font-medium text-slate-800">Tipo de OA</label>
            <select id={ids.tipo} className={INPUT_CLASS} value={oaTipo} onChange={(e) => setOaTipo(e.target.value as OATipo)}>
              {(Object.keys(OA_TIPO_LABEL) as OATipo[]).map((t) => <option key={t} value={t}>{OA_TIPO_LABEL[t]}</option>)}
            </select>
            <p className="text-[13px] text-slate-600 mt-1.5">Ajusta cuánto pesa cada dimensión Marzano.</p>
          </div>
        )}
      </Card>

      <Card title="3. Cómo será la evaluación">
        <fieldset>
          <legend className="text-sm font-medium text-slate-800 mb-2">Modalidad</legend>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {(Object.keys(MODALITY_LABEL) as Modality[]).map((m) => (
              <label key={m} className={`cursor-pointer rounded-lg border-2 px-3 py-3 ${modality === m ? 'border-marzano-600 bg-marzano-50' : 'border-slate-200 hover:border-slate-300'} has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-amber-500`}>
                <input type="radio" name="pilot-modality" className="sr-only" checked={modality === m} onChange={() => setModality(m)} />
                <span className="block text-sm font-semibold text-slate-900">{MODALITY_LABEL[m]}</span>
                <span className="block text-[13px] text-slate-600 mt-0.5">Máx. {MODALITY_MAX_MIN[m]} min</span>
              </label>
            ))}
          </div>
        </fieldset>
      </Card>

      <LessonMaterialUploader oaCodigo={primarySelectedOA?.code ?? null} oaDescripcion={primarySelectedOA?.description ?? null}
        nivelAcademico={learner?.level_label ?? null} asignatura={selectedSubject?.name ?? null} onReady={setLessonMaterial} />

      <details className="group rounded-xl border border-slate-200 bg-white" open={teacherHint.length > 0 || undefined}>
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4">
          <span><span className="block text-base font-semibold text-slate-900">Consideraciones para la IA</span><span className="block text-sm text-slate-600">Opcional · se aplica a las preguntas y a cada turno</span></span>
          <ChevronDown aria-hidden className="w-4 h-4 text-slate-500 transition-transform group-open:rotate-180" />
        </summary>
        <div className="px-5 pb-5">
          <label htmlFor={ids.hint} className="sr-only">Consideraciones para la IA</label>
          <textarea id={ids.hint} className={INPUT_CLASS + ' resize-y !mt-0'} rows={3} maxLength={500} placeholder="Ej: Haz las preguntas en inglés B1." value={teacherHint} onChange={(e) => setTeacherHint(e.target.value)} />
          <div className="text-xs text-slate-600 text-right tabular-nums mt-1">{teacherHint.length}/500</div>
        </div>
      </details>

      {error && <Alert kind="error">{error}</Alert>}

      <div className="fixed bottom-0 inset-x-0 lg:left-64 z-20 border-t border-slate-200 bg-white/95 backdrop-blur px-4 sm:px-8 py-3">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-3">
          {formValid && pregen.status !== 'idle' && !isLoading ? <PregenProgress pregen={pregen} /> : (
            <p className="text-[13px] text-slate-600">{formValid ? 'Listo para preparar la matriz.' : 'Completa alumno, asignatura, hito y al menos un OA.'}</p>
          )}
          <Button type="submit" disabled={!formValid || pregenInFlight} loading={isLoading}
            leftIcon={!isLoading && pregenInFlight ? <Loader2 aria-hidden className="w-4 h-4 animate-spin" /> : undefined}
            rightIcon={!pregenInFlight ? <ArrowRight aria-hidden className="w-4 h-4" /> : undefined}>
            {isLoading ? 'Iniciando evaluación…' : pregenInFlight ? 'Preparando matriz…' : pregen.status === 'ready' ? 'Iniciar evaluación' : 'Generar matriz e iniciar'}
          </Button>
        </div>
      </div>
    </form>
  );
}

const DIMS = [
  { k: 'recuperacion', n: 1, label: 'Recuperación' },
  { k: 'comprension', n: 2, label: 'Comprensión' },
  { k: 'analisis', n: 3, label: 'Análisis' },
  { k: 'transferencia', n: 4, label: 'Transferencia' },
] as const;

function PregenProgress({ pregen }: { pregen: ReturnType<typeof useMatrixPregeneration> }) {
  const ready = pregen.status === 'ready';
  const error = pregen.status === 'error';
  return (
    <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px]">
      <span className={`inline-flex items-center gap-1.5 font-semibold ${ready ? 'text-emerald-800' : error ? 'text-amber-900' : 'text-slate-800'}`}>
        {ready ? <CheckCircle2 aria-hidden className="w-4 h-4" /> : error ? <AlertCircle aria-hidden className="w-4 h-4" /> : <Loader2 aria-hidden className="w-4 h-4 animate-spin" />}
        {ready ? 'Matriz lista' : error ? 'No se pudo preparar; se intentará al iniciar' : 'Preparando matriz…'}
      </span>
      {!error && (
        <ul className="flex gap-1.5" aria-label="Dimensiones listas">
          {DIMS.map((d) => {
            const done = pregen.dimsReady.includes(d.k as 'recuperacion');
            return (
              <li key={d.k} title={d.label} className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs ${done ? 'bg-emerald-100 text-emerald-900' : 'bg-slate-100 text-slate-600'}`}>
                {done ? <CheckCircle2 aria-hidden className="w-3 h-3" /> : <span aria-hidden className="w-3 h-3 rounded-full border border-slate-400" />}{d.n}<span className="sr-only"> {d.label} {done ? 'lista' : 'pendiente'}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Derived({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3.5 py-2.5 min-w-0">
      <dt className="text-xs text-slate-600">{label}</dt>
      <dd className={`text-slate-900 truncate ${mono ? 'font-mono text-[13px]' : 'font-medium'}`} title={value}>{value}</dd>
    </div>
  );
}
